import { useCallback, useState } from "react";
import type {
  PendingGrantAddition,
  PendingGrantRemoval,
  PendingGrantUpdate,
} from "admin/types/interfaces/grant";
import { userPrincipal } from "admin/utils/users";
import { apiMutate, getApiErrorMessage, isApiError } from "~/utils/apiClient";

// user: ids are lowercased to match the server's normalized principals (and the
// principals built by AdminGrantsPanel's callbacks); user-tag: names are case-sensitive.
function pendingPrincipal(a: PendingGrantAddition): string {
  return a.type === "user" ? userPrincipal(a.identifier) : `user-tag:${a.identifier}`;
}

export interface GrantFlushResult {
  failedLabels: string[];
}

interface PendingGrantsState {
  additions: PendingGrantAddition[];
  removals: PendingGrantRemoval[];
  updates: PendingGrantUpdate[];
}

const EMPTY_PENDING: PendingGrantsState = { additions: [], removals: [], updates: [] };

export function usePendingGrants() {
  // All three pending lists live in one state object so handlers can read and rewrite
  // every slice atomically inside a single functional update. With separate useState
  // calls, `onRemoveAccess`/`onUpdateAccess` branched on a stale `additions` snapshot
  // when several edits landed in the same React batch, silently dropping or mis-routing
  // a pending grant.
  const [pending, setPending] = useState<PendingGrantsState>(EMPTY_PENDING);

  const pendingGrantAdditions = pending.additions;
  const pendingGrantRemovals = pending.removals;
  const pendingGrantUpdates = pending.updates;

  const hasGrantChanges =
    pendingGrantAdditions.length > 0 ||
    pendingGrantRemovals.length > 0 ||
    pendingGrantUpdates.length > 0;

  const resetGrants = useCallback(() => setPending(EMPTY_PENDING), []);

  const onAddAccess = (addition: PendingGrantAddition) =>
    setPending((p) => ({ ...p, additions: [...p.additions, addition] }));

  const onRemoveAccess = (removal: PendingGrantRemoval) =>
    setPending((p) => {
      const isPending = p.additions.some((a) => pendingPrincipal(a) === removal.principal);
      if (isPending) {
        return {
          ...p,
          additions: p.additions.filter((a) => pendingPrincipal(a) !== removal.principal),
        };
      }
      // A removal supersedes any pending update for the same grant — drop it so flush
      // doesn't both re-grant (update) and delete the same principal.
      return {
        ...p,
        updates: p.updates.filter(
          (u) => !(u.principal === removal.principal && u.oldPermission === removal.permission)
        ),
        removals: [...p.removals, removal],
      };
    });

  const onUpdateAccess = (update: PendingGrantUpdate) =>
    setPending((p) => {
      const isPending = p.additions.some((a) => pendingPrincipal(a) === update.principal);
      if (isPending) {
        return {
          ...p,
          additions: p.additions.map((a) =>
            pendingPrincipal(a) === update.principal
              ? { ...a, permission: update.newPermission }
              : a
          ),
        };
      }
      // If the user picked a permission after hitting Remove, undo the removal first
      const removals = p.removals.filter(
        (r) => !(r.principal === update.principal && r.permission === update.oldPermission)
      );
      const rest = p.updates.filter(
        (x) => !(x.principal === update.principal && x.oldPermission === update.oldPermission)
      );
      // Re-selecting the original permission only undoes the removal — no update to record
      const updates = update.newPermission === update.oldPermission ? rest : [...rest, update];
      return { ...p, removals, updates };
    });

  const flushGrants = async (grantsUrl: string): Promise<GrantFlushResult> => {
    const failedLabels: string[] = [];
    const failedAdditions: PendingGrantAddition[] = [];
    const failedUpdates: PendingGrantUpdate[] = [];
    const failedRemovals: PendingGrantRemoval[] = [];

    for (const addition of pendingGrantAdditions) {
      try {
        await apiMutate(grantsUrl, "POST", {
          principal: pendingPrincipal(addition),
          permission: addition.permission,
        });
      } catch (e) {
        const reason = isApiError(e) ? getApiErrorMessage(e) : "";
        failedLabels.push(reason ? `${addition.label}: ${reason}` : addition.label);
        failedAdditions.push(addition);
      }
    }

    for (const update of pendingGrantUpdates) {
      try {
        // POST first so a failure leaves the user overprovisioned rather than locked out
        await apiMutate(grantsUrl, "POST", {
          principal: update.principal,
          permission: update.newPermission,
        });
        await apiMutate(
          grantsUrl,
          "DELETE",
          { principal: update.principal, permission: update.oldPermission },
          { expects204: true }
        );
      } catch (e) {
        const reason = isApiError(e) ? getApiErrorMessage(e) : "";
        failedLabels.push(reason ? `${update.label}: ${reason}` : update.label);
        failedUpdates.push(update);
      }
    }

    for (const removal of pendingGrantRemovals) {
      try {
        await apiMutate(
          grantsUrl,
          "DELETE",
          { principal: removal.principal, permission: removal.permission },
          { expects204: true }
        );
      } catch (e) {
        const reason = isApiError(e) ? getApiErrorMessage(e) : "";
        failedLabels.push(reason ? `${removal.label}: ${reason}` : removal.label);
        failedRemovals.push(removal);
      }
    }

    // Prune succeeded items so they don't re-appear as pending on the next save
    setPending({
      additions: failedAdditions,
      removals: failedRemovals,
      updates: failedUpdates,
    });

    return { failedLabels };
  };

  return {
    pendingGrantAdditions,
    pendingGrantRemovals,
    pendingGrantUpdates,
    hasGrantChanges,
    resetGrants,
    onAddAccess,
    onRemoveAccess,
    onUpdateAccess,
    flushGrants,
  };
}
